import { supabase } from "@/integrations/supabase/client";
import type {
  CreateFormSessionRequest,
  GetUserSessionsResponse,
  UpsertFormSessionRequest,
  UpsertFormSessionResponse,
} from "../types/formSessions";
import { handleSupabaseFunctionError } from "../../utils/supabaseFunctionsError";
import { FormSession } from "../../types/database";

export async function createFormSession({
  payload,
}: {
  payload?: CreateFormSessionRequest;
}): Promise<FormSession> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    // Call backend API to create session
    const { data, error } = await supabase.functions.invoke(
      "api/sessions/create",
      {
        method: "POST",
        body: JSON.stringify(payload || {}),
      }
    );

    // Handle errors
    await handleSupabaseFunctionError(error);

    // Check for failure in response (fallback for backends that return 200 with success: false)
    if (!data?.success) {
      throw new Error(data?.error || "Failed to create session");
    }

    console.log(`📝 [SESSION] Created new session: ${data.session?.id}`);

    return data.session;
  } catch (error) {
    console.error("Error in createNewUserSession:", error);
    throw error;
  }
}

export async function upsertFormSession(
  request: UpsertFormSessionRequest
): Promise<UpsertFormSessionResponse> {
  try {
    const { sessionId, updates } = request;

    const { data: updatedSession, error: sessionError } = await supabase
      .from("form_sessions")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    if (sessionError) {
      console.error(
        "❌ [FormSession] Error upserting form session:",
        sessionError
      );
      return {
        success: false,
        error: sessionError.message || "Failed to upsert form session",
      };
    }

    console.log(
      `✅ [FormSession] Successfully upserted session: ${updatedSession.id}`
    );

    return {
      success: true,
      session: updatedSession,
    };
  } catch (error) {
    console.error("❌ [FormSession] Unexpected error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

export async function getUserSessions(
  userId: string,
  options: { includeArchived?: boolean } = {}
): Promise<GetUserSessionsResponse> {
  const { includeArchived = false } = options;

  let query = supabase
    .from("form_sessions")
    .select("id, status, created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (!includeArchived) {
    query = query.is("archived_at", null);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching user sessions:", error);
    throw error;
  }

  return data || [];
}
